import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  input,
  output,
  signal,
} from '@angular/core';
import {
  StatsPeriodPreset,
  StatsPeriodSelection,
} from '../admin-stats-aggregate/admin-stats-aggregate.types';
import {
  clampDateToMax,
  formatUtcDateOnly,
  getThisMonthRangeUtc,
  getThisWeekRangeUtc,
  isValidStatsDateRange,
} from '../admin-stats-period/admin-stats-period.helpers';

@Component({
  selector: 'admin-stats-period-filter',
  templateUrl: './admin-stats-period-filter.html',
  host: { class: 'block shrink-0' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminStatsPeriodFilter implements OnInit {
  /** Inclusive ceiling for the To date. Omit to allow today UTC. */
  readonly maxEndDate = input<string | null>(null);
  readonly periodChange = output<StatsPeriodSelection>();

  readonly preset = signal<StatsPeriodPreset>('this-week');
  readonly customStartDate = signal('');
  readonly customEndDate = signal('');
  readonly latestSelectableDate = computed(
    () => this.maxEndDate() ?? formatUtcDateOnly(new Date()),
  );
  readonly startDateMax = computed(() => {
    const endDate = this.customEndDate();
    const latest = this.latestSelectableDate();
    return endDate && endDate < latest ? endDate : latest;
  });

  ngOnInit(): void {
    this.selectPreset('this-week');
  }

  selectPreset(nextPreset: StatsPeriodPreset): void {
    this.preset.set(nextPreset);
    const now = this.referenceNow();

    if (nextPreset === 'this-week') {
      const week = getThisWeekRangeUtc(now);
      this.customStartDate.set(week.startDate);
      this.customEndDate.set(week.endDate);
      this.emitSelection('this-week', week);
      return;
    }

    if (nextPreset === 'this-month') {
      const month = getThisMonthRangeUtc(now);
      this.customStartDate.set(month.startDate);
      this.customEndDate.set(month.endDate);
      this.emitSelection('this-month', month);
      return;
    }

    const latest = this.latestSelectableDate();
    const startDate = clampDateToMax(this.customStartDate() || latest, latest);
    const endDate = clampDateToMax(this.customEndDate() || latest, latest);
    this.customStartDate.set(startDate > endDate ? endDate : startDate);
    this.customEndDate.set(endDate);
    this.emitCustomIfValid();
  }

  onCustomStartInput(event: Event): void {
    const inputEl = event.target as HTMLInputElement;
    const endDate = this.customEndDate();
    const latest = this.startDateMax();
    const value = clampDateToMax(
      endDate && inputEl.value > endDate ? endDate : inputEl.value,
      latest,
    );
    inputEl.value = value;
    this.customStartDate.set(value);
    this.preset.set('custom');
    this.emitCustomIfValid();
  }

  onCustomEndInput(event: Event): void {
    const inputEl = event.target as HTMLInputElement;
    const startDate = this.customStartDate();
    const latest = this.latestSelectableDate();
    let value = clampDateToMax(inputEl.value, latest);
    if (startDate && value && value < startDate) {
      if (startDate > latest) {
        this.customStartDate.set(latest);
        value = latest;
      } else {
        value = startDate;
      }
    }
    inputEl.value = value;
    this.customEndDate.set(value);
    this.preset.set('custom');
    this.emitCustomIfValid();
  }

  getPresetButtonClass(nextPreset: StatsPeriodPreset): string {
    const active = this.preset() === nextPreset;

    if (active) {
      return 'border-violet-500 bg-violet-50 text-violet-700 dark:border-violet-400 dark:bg-violet-500/20 dark:text-violet-100';
    }

    return 'border-zinc-200 text-zinc-600 hover:border-zinc-300 hover:bg-zinc-50 dark:border-white/10 dark:text-zinc-400 dark:hover:border-white/20 dark:hover:bg-white/5 dark:hover:text-zinc-200';
  }

  private emitCustomIfValid(): void {
    const startDate = this.customStartDate();
    const endDate = this.customEndDate();

    if (!isValidStatsDateRange(startDate, endDate)) {
      return;
    }

    this.emitSelection('custom', { startDate, endDate });
  }

  private emitSelection(preset: StatsPeriodPreset, range: { startDate: string; endDate: string }): void {
    const latest = this.latestSelectableDate();
    const endDate = clampDateToMax(range.endDate, latest);
    const startDate = clampDateToMax(range.startDate, endDate);

    this.periodChange.emit({
      preset,
      startDate,
      endDate,
    });
  }

  private referenceNow(): Date {
    const maxEndDate = this.maxEndDate();
    if (!maxEndDate) {
      return new Date();
    }

    return new Date(`${maxEndDate}T12:00:00.000Z`);
  }
}
